import { Carousel, CarouselItem } from '@ixigo/iui/Carousel';
import Chip from '@ixigo/iui/Chip';
import Typography from '@ixigo/iui/Typography';
import { useMemo, useState } from 'react';
import type { HomepageOffer, Offer as OfferType } from '../-api/offers';

function OfferCard({ offer }: { offer: OfferType }) {
	return (
		<div className="h-auto rounded-20 overflow-hidden">
			<a
				href={offer.url}
				target="_blank"
				rel="noopener noreferrer"
				className="block relative"
			>
				<img
					src={offer.banner}
					alt={offer.offerTitle}
					className="w-full h-full max-h-[180px] object-cover"
				/>
				<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
			</a>
		</div>
	);
}

type OfferTabProps = {
	uniqueTags: string[];
	activeTag: string | null;
	setActiveTag: (tag: string | null) => void;
};

const OfferTabs = ({ uniqueTags, activeTag, setActiveTag }: OfferTabProps) => {
	return (
		<div className="pl-20 flex gap-10 whitespace-nowrap overflow-x-scroll scrollbar-hide">
			{uniqueTags.map((tag) => {
				const active = tag === activeTag;
				return (
					<Chip
						key={tag}
						variant={active ? 'solid' : 'outlined'}
						color="neutral"
						size="lg"
						onClick={() => setActiveTag(active ? null : tag)}
					>
						{tag}
					</Chip>
				);
			})}
		</div>
	);
};

export function OffersSection({ offers }: { offers: Array<HomepageOffer> }) {
	// defaultSelectedFilter should be Cabs
	const defaultSelectedFilter =
		offers.find((offer) => offer.isDefaultSelected)?.tag || 'All';
	const [activeFilter, setActiveFilter] = useState<string | null>(
		defaultSelectedFilter,
	);

	const uniqueTags = Array.from(new Set(offers.map((offer) => offer.tag)));

	const filteredOffers = useMemo(() => {
		return activeFilter === 'All'
			? offers.flatMap((offer) => offer.deals)
			: offers
					.filter((offer) => offer.tag === activeFilter)
					.flatMap((offer) => offer.deals);
	}, [offers, activeFilter]);

	return (
		<div className="bg-primary py-20 pr-0 flex flex-col gap-y-10">
			<Typography variant="h4" fontWeight="medium" className="pl-20">
				Offers For You
			</Typography>
			<div className="flex flex-col gap-y-10">
				{uniqueTags.length > 1 && (
					<OfferTabs
						uniqueTags={uniqueTags}
						activeTag={activeFilter}
						setActiveTag={setActiveFilter}
					/>
				)}

				<Carousel
					indicatorsType={filteredOffers.length > 1 ? 'slide' : false}
					scrollSnapAlign="center"
				>
					{filteredOffers.map((offer) => (
						<CarouselItem
							key={offer.id}
							className={`last:mr-20 first:ml-20 ${
								filteredOffers.length > 1 ? 'w-[270px] ' : 'w-full px-20'
							}`}
						>
							<OfferCard offer={offer} />
						</CarouselItem>
					))}
				</Carousel>
			</div>
		</div>
	);
}
